import { Component } from '@verndale/core';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { BREAKPOINTS } from '../constants';
import { debounce } from '../helpers';

class Module extends Component {
  setupDefaults() {
    this.dom = {
      body: document.body,
      dropdownElements: this.el.querySelectorAll(
        '.secondary-navigation__item-btn'
      ),
      subMenuElements: this.el.querySelectorAll(
        '.secondary-navigation__dropdown-content'
      ),
      hamburgerBtn: this.el.querySelector('.header__toggle-btn'),
      directLinks: this.el.querySelectorAll(
        '.primary-navigation__item-link, .secondary-navigation__item-link'
      ),
      navigationWrapper: this.el.querySelector('.header__navigation-wrapper'),
      headerContainer: this.el.querySelector('.header__container'),
      secondaryNavigationContainer: this.el.querySelector(
        '.secondary-navigation__items'
      ),
      megaMenu: this.el.querySelector('.header__mega-menu'),
      navButtons: this.el.querySelectorAll('.primary-navigation__item-link'),
      categoryButtons: this.el.querySelectorAll(
        '.header__mega-menu-left-content-btn'
      ),
      categoryContents: this.el.querySelectorAll(
        '.header__mega-menu-right-content'
      ),
      categoryList: this.el.querySelector(
        '.header__mega-menu-left-content-list'
      ),
      backBtn: this.el.querySelector('.header__mega-menu-back-btn'),
      megaMenuLinks: this.el.querySelectorAll('.header__mega-menu a[href]')
    };

    this.headerHeight = this.el.offsetHeight;
    this.isMobile = !window.matchMedia(
      `(min-width: ${BREAKPOINTS.TABLET_LANDSCAPE}px)`
    ).matches;
    this.visibleDropdownClass = 'secondary-navigation-dropdown-visible';

    // Create slider element
    this.createSlider();
  }

  addListeners() {
    window.addEventListener('scroll', this.handleScroll.bind(this));

    window.addEventListener(
      'resize',
      debounce(() => {
        this.handleReset();

        // Update mobile state
        this.isMobile = window.innerWidth <= 1279;

        // Update slider position if mobile and mega menu is visible
        if (
          this.isMobile &&
          this.dom.megaMenu.classList.contains('is-visible')
        ) {
          const activeButton =
            this.el.querySelector(
              '.header__mega-menu-left-content-btn.is-active'
            ) || this.dom.categoryButtons[0];
          this.updateSliderPosition(activeButton);
        }
      }, 50)
    );

    // Add escape key listener
    document.addEventListener('keydown', e => {
      if (
        e.key === 'Escape' &&
        this.dom.megaMenu.classList.contains('is-visible')
      ) {
        this.closeMegaMenu();
      }
    });

    this.dom.dropdownElements.forEach(el => {
      el.addEventListener('click', this.handleDropdownClick.bind(this));
    });

    this.dom.navButtons.forEach(button => {
      button.addEventListener('click', this.toggleMegaMenu.bind(this));
    });

    this.dom.categoryButtons.forEach(button => {
      button.addEventListener('click', this.switchCategory.bind(this));
    });

    this.dom.hamburgerBtn.addEventListener(
      'click',
      this.handleMobileMenu.bind(this)
    );

    // Add back button listener
    if (this.dom.backBtn) {
      this.dom.backBtn.addEventListener('click', this.closeMegaMenu.bind(this));
    }

    // Add listeners to all links inside mega menu
    if (this.dom.megaMenuLinks) {
      this.dom.megaMenuLinks.forEach(link => {
        // Only add listener if it's a real link (has href) and not a button
        if (
          link.getAttribute('href') &&
          !link.classList.contains('header__mega-menu-left-content-btn')
        ) {
          link.addEventListener('click', () => {
            this.closeMegaMenu();
          });
        }
      });
    }

    document.addEventListener('click', this.handleClickOutside.bind(this));
  }

  createSlider() {
    if (!this.dom.categoryList) return;

    const slider = document.createElement('div');
    slider.className = 'header__mega-menu-left-content-slider';
    this.dom.categoryList.appendChild(slider);
    this.dom.slider = slider;
  }

  updateSliderPosition(activeButton) {
    if (!this.dom.slider || !this.isMobile) return;

    const buttonRect = activeButton.getBoundingClientRect();
    const listRect = this.dom.categoryList.getBoundingClientRect();
    const left = buttonRect.left - listRect.left;

    // Match button dimensions and position exactly
    this.dom.slider.style.transform = `translateX(${left - 3}px)`;
    this.dom.slider.style.width = `${buttonRect.width}px`;
    this.dom.slider.style.height = `${buttonRect.height}px`;
    this.dom.slider.style.top = `${buttonRect.top - listRect.top}px`;
  }

  switchCategory(e) {
    const { category } = e.currentTarget.dataset;

    // Toggle active state on buttons
    this.dom.categoryButtons.forEach(button => {
      button.classList.remove('is-active');
    });
    e.currentTarget.classList.add('is-active');

    // Update slider position
    if (this.isMobile) {
      this.updateSliderPosition(e.currentTarget);
    }

    // First, fade out any currently visible content
    const currentlyVisible = Array.from(this.dom.categoryContents).find(
      content =>
        content.style.display === 'flex' || content.style.display === ''
    );

    if (currentlyVisible && currentlyVisible.dataset.category !== category) {
      // Fade out current content
      currentlyVisible.classList.add('fade-out');

      // After fade-out completes, hide it and show new content
      setTimeout(() => {
        currentlyVisible.style.display = 'none';
        currentlyVisible.classList.remove('fade-out', 'fade-in');

        // Now show and fade in the new content
        this.showNewContent(category);
      }, 300); // Wait for fade-out animation to complete
    } else {
      // No current content visible, show new content immediately
      this.showNewContent(category);
    }
  }

  showNewContent(category) {
    // Hide all content first
    this.dom.categoryContents.forEach(content => {
      if (content.dataset.category !== category) {
        content.style.display = 'none';
        content.classList.remove('fade-out', 'fade-in');
      }
    });

    // Show and animate in the new content
    const newContent = Array.from(this.dom.categoryContents).find(
      content => content.dataset.category === category
    );

    if (newContent) {
      newContent.style.display = 'flex';
      newContent.classList.add('fade-out'); // Start with fade-out state

      // Small delay to ensure display is applied, then fade in
      setTimeout(() => {
        newContent.classList.remove('fade-out');
        newContent.classList.add('fade-in');
      }, 50);

      // Remove fade-in class after animation completes
      setTimeout(() => {
        newContent.classList.remove('fade-in');
      }, 350);
    }
  }

  toggleMegaMenu(e) {
    e.preventDefault();
    const currentButton = e.currentTarget;

    this.dom.navButtons.forEach(button => {
      button.classList.remove('active');
    });

    if (this.dom.megaMenu.classList.contains('is-visible')) {
      this.closeMegaMenu();
    } else {
      // Check and set body overflow
      if (this.dom.body.style.overflow !== 'hidden') {
        this.dom.body.style.overflow = 'hidden';
      }

      this.dom.megaMenu.classList.add('is-visible');
      currentButton.classList.add('active');

      const firstCategoryButton = this.dom.categoryButtons[0];
      const { category } = firstCategoryButton.dataset;

      this.dom.categoryButtons.forEach(button => {
        button.classList.remove('is-active');
      });
      firstCategoryButton.classList.add('is-active');

      if (this.isMobile) {
        this.updateSliderPosition(firstCategoryButton);
      }

      // Show first category using the same method
      this.showNewContent(category);
    }
  }

  handleClickOutside(event) {
    if (
      !event.target.closest('.primary-navigation__item-link') &&
      !event.target.closest('.header__mega-menu')
    ) {
      if (this.dom.megaMenu.classList.contains('is-visible')) {
        this.closeMegaMenu();
      }
    }

    this.dom.dropdownElements.forEach(el => {
      const isClickInside = el.contains(event.target);
      const isClickInsideHeader =
        this.dom.secondaryNavigationContainer.contains(event.target);
      if (!isClickInside) {
        el.nextElementSibling.classList.remove(this.visibleDropdownClass);
        this.dom.directLinks.forEach(link => {
          link.classList.remove('link-non-active');
        });
      }
      if (!isClickInsideHeader) {
        this.el.style.height = '';
      }
    });
  }

  handleMobileMenu() {
    if (this.el.classList.contains('header-mobile-isopen')) {
      enableBodyScroll(this.dom.navigationWrapper);
    } else {
      disableBodyScroll(this.dom.navigationWrapper, {
        allowTouchMove: () => true
      });
    }
    this.el.classList.toggle('header-mobile-isopen');
    this.dom.subMenuElements.forEach(el => {
      el.classList.remove(this.visibleDropdownClass);
    });
  }

  handleDropdownClick(e) {
    const subMenu = e.target.nextElementSibling;
    subMenu.classList.toggle(this.visibleDropdownClass);
    const headerContainerPadding = parseInt(
      window
        .getComputedStyle(this.dom.headerContainer, null)
        .getPropertyValue('padding-block-end'),
      10
    );

    // Deals with header height when a dropdown is active
    if (
      !this.isMobile &&
      subMenu.classList.contains(this.visibleDropdownClass)
    ) {
      const dropdownHeight = subMenu.offsetHeight;
      this.el.style.height = `${
        this.headerHeight + dropdownHeight - headerContainerPadding
      }px`;
    } else {
      this.el.style.height = '';
    }

    // Deals with link opacity, when dropdown is active
    const visibleElement = this.el.querySelector(
      `.${this.visibleDropdownClass}`
    );
    if (visibleElement) {
      this.dom.directLinks.forEach(link => {
        if (
          !visibleElement.contains(link) &&
          !visibleElement.previousElementSibling.contains(link)
        ) {
          link.classList.add('link-non-active');
        }
      });
    } else {
      this.dom.directLinks.forEach(link => {
        link.classList.remove('link-non-active');
      });
    }
  }

  handleScroll() {
    if (
      document.body.scrollTop > 135 ||
      document.documentElement.scrollTop > 135
    ) {
      this.el.classList.add('header-scroll');
    } else {
      this.el.classList.remove('header-scroll');
    }
    this.handleReset();
  }

  handleReset() {
    this.el.style.height = '';
    this.el.classList.remove('header-mobile-isopen');
    enableBodyScroll(this.dom.body);
    const activeDropdowns = this.el.querySelectorAll(
      `.${this.visibleDropdownClass}`
    );
    const nonActiveLinks = this.el.querySelectorAll(`.link-non-active`);

    if (activeDropdowns && nonActiveLinks) {
      activeDropdowns.forEach(el => {
        el.classList.remove(this.visibleDropdownClass);
      });

      nonActiveLinks.forEach(el => {
        el.classList.remove('link-non-active');
      });
    }

    // Also reset body overflow if mega menu is not visible
    if (!this.dom.megaMenu.classList.contains('is-visible')) {
      this.dom.body.style.overflow = '';
    }
  }

  closeMegaMenu() {
    this.dom.megaMenu.classList.remove('is-visible');
    this.dom.navButtons.forEach(button => {
      button.classList.remove('active');
    });

    // Reset body overflow only if mobile menu is not open
    if (!this.el.classList.contains('header-mobile-isopen')) {
      this.dom.body.style.overflow = '';
    }
  }
}

export default Module;
